import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LEVELS = [
  { key: "partner", name: "Partner", amount: 20, blurb: "Stand with the ministry every month and help share the message of the transformed mind." },
  { key: "builder", name: "Builder", amount: 100, blurb: "Help build new teachings, the free discipleship program and outreach that reaches more lives.", featured: true },
  { key: "kingdom", name: "Kingdom Partner", amount: 500, blurb: "Make a Kingdom-sized impact. Includes a dedicated life coaching connection with live Zoom sessions and email answers." },
];

const useGive = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const [busy, setBusy] = useState("");
  const give = async (amount, key, once) => {
    if (!user) return nav("/register?next=/partnership");
    setBusy(key);
    try {
      const { data } = await api.post("/billing/checkout", { kind: "partner", amount, interval: once ? "once" : "month", origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(""); }
  };
  return { give, busy, user };
};

const LevelCard = ({ l, give, busy, current, once }) => (
  <div className={`card-lux p-8 flex flex-col relative ${l.featured ? "border-[#D4AF37]/60" : ""}`} data-testid={`partner-card-${l.key}`}>
    {l.featured && <span className="absolute -top-3 left-8 bg-[#D4AF37] text-black text-xs font-bold px-3 py-1 rounded-full inline-flex items-center gap-1"><Star size={12} />Most chosen</span>}
    <p className="text-xs uppercase tracking-widest text-gold-light font-accent">{once ? "One-time gift" : l.name}</p>
    <p className="font-serif text-5xl text-white mt-3" data-testid={`partner-amount-${l.key}`}>${l.amount}<span className="text-base text-slate-400">{once ? " once" : " / month"}</span></p>
    <p className="text-sm text-slate-400 mt-4 flex-1">{once ? "A single gift to support the ministry and help the message reach more lives." : l.blurb}</p>
    <button disabled={!!busy || (current && !once)} onClick={() => give(l.amount, l.key, once)} className={l.featured ? "btn-gold mt-8" : "btn-outline mt-8"} data-testid={`partner-give-${l.key}`}>
      <Heart size={15} />{current && !once ? "Your current gift" : busy === l.key ? "Redirecting..." : once ? `Give $${l.amount} once` : `Give $${l.amount} monthly`}
    </button>
  </div>
);

const Toggle = ({ once, setOnce }) => (
  <div className="flex justify-center mb-10">
    <div className="inline-flex p-1 rounded-full border border-white/15 bg-[#0A0F1D]" role="tablist" data-testid="gift-frequency-toggle">
      {[["Monthly", false], ["One-time", true]].map(([label, v]) => (
        <button key={label} role="tab" aria-selected={once === v} onClick={() => setOnce(v)}
          className={`px-6 py-2 rounded-full text-sm font-semibold transition-colors ${once === v ? "bg-[#D4AF37] text-black" : "text-slate-300 hover:text-white"}`}
          data-testid={`gift-${v ? "once" : "monthly"}-tab`}>{label}</button>
      ))}
    </div>
  </div>
);

const CustomAmount = ({ give, busy, once }) => {
  const [amt, setAmt] = useState("");
  const n = parseFloat(amt);
  const ok = n >= 5 && n <= 10000;
  return (
    <div className="card-lux p-6 sm:p-8 mt-8 flex flex-col md:flex-row md:items-center gap-6 justify-between" data-testid="partner-custom">
      <div>
        <p className="font-serif text-2xl text-white">{once ? "Choose your own gift amount" : "Choose your own monthly amount"}</p>
        <p className="text-sm text-slate-400 mt-1">{once ? "Any amount from $5." : "Any amount from $5 a month. Gifts of $500 or more include the coaching connection."}</p>
      </div>
      <div className="flex gap-3 items-center">
        <div className="relative"><span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">$</span>
          <input type="number" min="5" max="10000" step="1" value={amt} onChange={(e) => setAmt(e.target.value)} placeholder="50" className="field !pl-8 w-32" data-testid="partner-custom-input" /></div>
        <button disabled={!ok || !!busy} onClick={() => give(n, "custom", once)} className="btn-gold" data-testid="partner-custom-give">{busy === "custom" ? "Redirecting..." : once ? "Give once" : "Give monthly"}</button>
      </div>
    </div>
  );
};

export default function PartnerCards() {
  const { give, busy, user } = useGive();
  const [once, setOnce] = useState(false);
  return (
    <div data-testid="partner-cards">
      <Toggle once={once} setOnce={setOnce} />
      <div className="grid md:grid-cols-3 gap-6">
        {LEVELS.map((l) => <LevelCard key={l.key} l={l} give={give} busy={busy} once={once} current={user?.subscription_status === "active" && user?.partner_amount === l.amount} />)}
      </div>
      <CustomAmount give={give} busy={busy} once={once} />
      <p className="text-xs text-slate-500 text-center mt-6">Secure giving through Stripe. Monthly gifts can be changed or canceled anytime from your dashboard.</p>
    </div>
  );
}
